import { useEffect, useRef } from 'react';

type UsePersistentConditionArgs = {
  /** The boolean condition to monitor. The callback is triggered if this remains true. */
  condition: boolean;
  /** The duration in milliseconds the condition must remain true before the callback is triggered. */
  durationMs: number;
  /** The function to call when the condition has remained true for the specified duration. */
  callback: () => void;
};

/**
 * Executes a callback function if a specified condition remains true for a given duration.
 */
export const usePersistentCondition = ({
  condition,
  durationMs,
  callback,
}: UsePersistentConditionArgs) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (condition) {
      timer = setTimeout(() => {
        callbackRef.current();
      }, durationMs);
    }

    return () => {
      timer && clearTimeout(timer);
      timer = null;
    };
  }, [condition, durationMs]);
};
